import { CommonModule } from '@angular/common';
import { Component, OnDestroy, OnInit } from '@angular/core';
import { ActivatedRoute, Router, RouterModule } from '@angular/router';
import { formatDistanceToNow } from 'date-fns';
import { Subscription } from 'rxjs';

import { HeaderComponent } from '../../common/header/header.component';
import { SidebarComponent } from '../../common/slidebar/sidebar.components';
import {
  AppNotification,
  NotificationsService,
} from '../../../services/notifications/notifications.service';

const PAGE_LIMIT = 30;
const BODY_MAX_LEN = 140;

const ACCOUNT_NAV_TYPES = new Set([
  'pack_expiring',
  'pack_expired',
  'queued_activated',
  'queued_discarded',
]);

type FilterTab = 'all' | 'unread';

@Component({
  selector: 'app-notifications-page',
  standalone: true,
  imports: [CommonModule, RouterModule, HeaderComponent, SidebarComponent],
  templateUrl: './notifications-page.component.html',
  styleUrl: './notifications-page.component.css',
})
export class NotificationsPageComponent implements OnInit, OnDestroy {
  filter: FilterTab = 'all';
  isLoading = false;
  isLoadingMore = false;
  loadError = '';
  markingAll = false;
  deletingId: string | null = null;
  expandedId: string | null = null;
  focusId: string | null = null;
  notifications: AppNotification[] = [];
  totalCount = 0;
  page = 1;
  unreadCount = 0;

  private unreadSub?: Subscription;
  private listSub?: Subscription;
  private markSub?: Subscription;
  private deleteSub?: Subscription;
  private querySub?: Subscription;

  constructor(
    private notificationsService: NotificationsService,
    private router: Router,
    private route: ActivatedRoute,
  ) {}

  ngOnInit(): void {
    this.unreadSub = this.notificationsService.unreadCount$.subscribe(
      (count) => {
        this.unreadCount = count;
      },
    );
    this.notificationsService.ensurePolling();

    this.querySub = this.route.queryParamMap.subscribe((params) => {
      const id = params.get('id');
      this.focusId = id;
      this.expandedId = id;
      this.reload();
    });
  }

  ngOnDestroy(): void {
    this.unreadSub?.unsubscribe();
    this.listSub?.unsubscribe();
    this.markSub?.unsubscribe();
    this.deleteSub?.unsubscribe();
    this.querySub?.unsubscribe();
  }

  get hasMore(): boolean {
    return this.notifications.length < this.totalCount;
  }

  setFilter(next: FilterTab): void {
    if (this.filter === next) return;
    this.filter = next;
    this.focusId = null;
    this.expandedId = null;
    if (this.route.snapshot.queryParamMap.get('id')) {
      // queryParamMap subscription reloads after clearing ?id=
      void this.router.navigate([], {
        relativeTo: this.route,
        queryParams: {},
        replaceUrl: true,
      });
      return;
    }
    this.reload();
  }

  reload(): void {
    this.page = 1;
    this.loadPage(1, false);
  }

  loadMore(): void {
    if (this.isLoading || this.isLoadingMore || !this.hasMore) return;
    this.loadPage(this.page + 1, true);
  }

  private loadPage(page: number, append: boolean): void {
    this.listSub?.unsubscribe();
    if (append) {
      this.isLoadingMore = true;
    } else {
      this.isLoading = true;
      this.loadError = '';
    }

    this.listSub = this.notificationsService
      .getNotifications({
        page,
        limit: PAGE_LIMIT,
        unreadOnly: this.filter === 'unread',
      })
      .subscribe({
        next: (data) => {
          const rows = data.rows ?? [];
          this.notifications = append
            ? [...this.notifications, ...rows]
            : rows;
          this.totalCount = data.count ?? this.notifications.length;
          this.page = data.page ?? page;
          this.isLoading = false;
          this.isLoadingMore = false;
          this.applyFocusNotification();
        },
        error: () => {
          this.isLoading = false;
          this.isLoadingMore = false;
          if (!append) {
            this.loadError = 'Could not load notifications.';
          }
        },
      });
  }

  private applyFocusNotification(): void {
    if (!this.focusId) return;
    const match = this.notifications.find(
      (n) => n.notification_id === this.focusId,
    );
    if (!match) return;

    this.expandedId = match.notification_id;
    if (!match.is_read) {
      this.markSub?.unsubscribe();
      this.markSub = this.notificationsService
        .markAsRead(match.notification_id)
        .subscribe({
          next: () => {
            match.is_read = true;
          },
        });
    }

    // Scroll focused row into view after paint.
    setTimeout(() => {
      document
        .getElementById(`notification-${match.notification_id}`)
        ?.scrollIntoView({ behavior: 'smooth', block: 'center' });
    }, 50);
  }

  isExpanded(item: AppNotification): boolean {
    return this.expandedId === item.notification_id;
  }

  isFocused(item: AppNotification): boolean {
    return this.focusId === item.notification_id;
  }

  bodyNeedsExpand(body: string | null | undefined): boolean {
    return (body || '').trim().length > BODY_MAX_LEN;
  }

  displayBody(item: AppNotification): string {
    const text = (item.body || '').trim();
    if (!text) return '';
    if (this.isExpanded(item)) return text;
    return this.truncateBody(text);
  }

  onRowClick(item: AppNotification): void {
    const id = item.notification_id;
    const isExpanded = this.expandedId === id;
    const canExpand = this.bodyNeedsExpand(item.body);
    const goAccount = ACCOUNT_NAV_TYPES.has(item.type);

    const afterMark = () => {
      if (canExpand && !isExpanded) {
        this.expandedId = id;
        return;
      }
      if (canExpand && isExpanded) {
        this.expandedId = null;
        if (goAccount) {
          void this.router.navigate(['/user-account'], {
            queryParams: { tab: 'subscription' },
          });
        }
        return;
      }
      if (goAccount) {
        void this.router.navigate(['/user-account'], {
          queryParams: { tab: 'subscription' },
        });
      }
    };

    if (item.is_read) {
      afterMark();
      return;
    }

    this.markSub?.unsubscribe();
    this.markSub = this.notificationsService.markAsRead(id).subscribe({
      next: () => {
        item.is_read = true;
        afterMark();
      },
      error: () => afterMark(),
    });
  }

  onMarkAllAsRead(): void {
    if (this.markingAll || this.unreadCount <= 0) return;
    this.markingAll = true;
    this.markSub?.unsubscribe();
    this.markSub = this.notificationsService.markAllAsRead().subscribe({
      next: () => {
        this.notifications = this.notifications.map((n) => ({
          ...n,
          is_read: true,
        }));
        this.markingAll = false;
        if (this.filter === 'unread') {
          this.reload();
        }
      },
      error: () => {
        this.markingAll = false;
      },
    });
  }

  onDeleteOne(item: AppNotification, event: Event): void {
    event.preventDefault();
    event.stopPropagation();
    if (this.deletingId) return;

    this.deletingId = item.notification_id;
    this.deleteSub?.unsubscribe();
    this.deleteSub = this.notificationsService
      .deleteNotification(item.notification_id)
      .subscribe({
        next: () => {
          if (this.expandedId === item.notification_id) {
            this.expandedId = null;
          }
          if (this.focusId === item.notification_id) {
            this.focusId = null;
            void this.router.navigate([], {
              relativeTo: this.route,
              queryParams: {},
              replaceUrl: true,
            });
          }
          this.notifications = this.notifications.filter(
            (n) => n.notification_id !== item.notification_id,
          );
          this.totalCount = Math.max(0, this.totalCount - 1);
          this.deletingId = null;
        },
        error: () => {
          this.deletingId = null;
        },
      });
  }

  truncateBody(body: string | null | undefined): string {
    const text = (body || '').trim();
    if (text.length <= BODY_MAX_LEN) return text;
    return `${text.slice(0, BODY_MAX_LEN - 1)}…`;
  }

  relativeTime(iso: string | null | undefined): string {
    if (!iso) return '';
    const date = new Date(iso);
    if (Number.isNaN(date.getTime())) return '';
    try {
      return formatDistanceToNow(date, { addSuffix: true });
    } catch {
      return '';
    }
  }

  trackById(_index: number, item: AppNotification): string {
    return item.notification_id;
  }
}
